import ImageOptimizer from '@/components/image-optimizer';
import type {
  WrittenNoticeSheetBullet,
  WrittenNoticeSheetRichLine,
  WrittenNoticeSheetSection,
} from '@/data/exam.notice.data';
import { cn } from '@/lib/utils';

interface Props {
  section: WrittenNoticeSheetSection;
}

function isBulletBlock(b: WrittenNoticeSheetBullet): b is {
  readonly line: WrittenNoticeSheetRichLine;
  readonly subLines?: readonly WrittenNoticeSheetRichLine[];
} {
  return (
    typeof b === 'object' && b !== null && !Array.isArray(b) && 'line' in b
  );
}

/** `{ text, emphasis }` 한 덩어리만으로 한 불릿 줄인 경우 (`{ line }` 블록과 구분) */
function isEmphasisLineBullet(
  b: WrittenNoticeSheetBullet,
): b is { readonly text: string; readonly emphasis?: 'bold' | 'extrabold' } {
  return (
    typeof b === 'object' &&
    b !== null &&
    !Array.isArray(b) &&
    'text' in b &&
    !('line' in b)
  );
}

function emphasisClass(emphasis?: 'bold' | 'extrabold'): string {
  if (emphasis === 'extrabold') return 'font-extrabold';
  if (emphasis === 'bold') return 'font-bold';
  return 'font-semibold';
}

function RichLine({
  line,
  className,
}: {
  line: WrittenNoticeSheetRichLine;
  className?: string;
}) {
  if (typeof line === 'string') {
    return (
      <span
        className={cn('min-w-0 whitespace-pre-line font-semibold', className)}
      >
        {line}
      </span>
    );
  }

  return (
    <span
      className={cn('min-w-0 whitespace-pre-line font-semibold', className)}
    >
      {line.map((part, i) => {
        if (typeof part === 'string') {
          return <span key={i}>{part}</span>;
        }
        return (
          <span key={i} className={emphasisClass(part.emphasis)}>
            {part.text}
          </span>
        );
      })}
    </span>
  );
}

function BulletBlock({ bullet }: { bullet: WrittenNoticeSheetBullet }) {
  if (isBulletBlock(bullet)) {
    return (
      <li className="flex flex-col gap-1.5 typo-15 leading-snug text-222222">
        <div className="flex gap-1">
          <span className="shrink-0 font-semibold">-</span>
          <RichLine line={bullet.line} />
        </div>
        {bullet.subLines?.map((line, j) => (
          <div
            key={j}
            className="flex gap-1 pl-2 typo-15 leading-snug text-333333"
          >
            <span className="shrink-0 font-semibold">·</span>
            <RichLine line={line} className="text-333333" />
          </div>
        ))}
      </li>
    );
  }

  const richLine: WrittenNoticeSheetRichLine = isEmphasisLineBullet(bullet)
    ? [bullet]
    : bullet;

  return (
    <li className="flex gap-1 typo-15 leading-snug text-222222">
      <span className="shrink-0 font-semibold">-</span>
      <RichLine line={richLine} />
    </li>
  );
}

/** 준비물 BottomSheet — 섹션 카드(이미지형 / 텍스트형) */
export default function WrittenNoticeSheetSectionCard({ section }: Props) {
  const { title, headerSub, bullets } = section;
  const showImage =
    section.type === 'image' && Boolean(section.imageSrc?.length);

  return (
    <section className="rounded-sm bg-white">
      <div className="flex min-h-[42px] flex-col items-center justify-center gap-1 rounded-t-sm bg-222222 py-2.5">
        <h3 className="typo-16 font-extrabold text-white">{title}</h3>
        {headerSub ? (
          <p className="whitespace-pre-line typo-13 font-semibold text-[#908E9E]">
            {headerSub}
          </p>
        ) : null}
      </div>

      <div className={showImage ? 'py-5' : 'px-4 py-4'}>
        {showImage ? (
          <div className="flex flex-col">
            <div
              className="relative w-full bg-white"
              style={{
                aspectRatio: `${section.imageAspect[0]} / ${section.imageAspect[1]}`,
              }}
            >
              <ImageOptimizer
                src={section.imageSrc}
                alt={`${title} 이미지`}
                fill
                sizes="(max-width: 1024px) 100vw, 500px"
                className="object-contain px-2"
              />
            </div>
          </div>
        ) : null}

        <ul
          className={
            showImage
              ? 'flex flex-col gap-1 px-2 pt-5'
              : 'flex flex-col gap-2.5'
          }
        >
          {bullets.map((b, i) => (
            <BulletBlock key={i} bullet={b} />
          ))}
        </ul>
      </div>
    </section>
  );
}
